// Extensible form-type registry. Add a new form by appending an entry here —
// the wizard, PDF, report view and history all render from this definition,
// so no other code needs to change to support a new form type.
import { SERVICE_SECTIONS, FINAL_STATUS_OPTIONS, withToggleRemarks } from "./reportTemplate";

// Field types: check | number | text | select | remarks
const RAW_COMMISSIONING_SECTIONS = [
  {
    id: "install_details",
    title: "Installation Details",
    description: "Equipment and installer details (heat pump model/serial come from the asset).",
    fields: [
      { type: "text", id: "cylinder_model", label: "Cylinder model" },
      { type: "text", id: "cylinder_serial", label: "Cylinder serial number" },
      { type: "text", id: "dhw_booster_capacity", label: "DHW tank booster heater capacity" },
      { type: "text", id: "coil_surface_area", label: "Coil surface area of cylinder" },
      { type: "text", id: "boiler_model", label: "Boiler model (bi-valent system, if any)" },
      { type: "text", id: "install_company", label: "Installation company name" },
      { type: "text", id: "install_company_address", label: "Installation company address" },
      { type: "remarks", id: "install_remarks", label: "Remarks" },
    ],
  },
  {
    id: "outdoor_unit",
    title: "Outdoor Unit",
    description: "Confirm the outdoor unit installation.",
    fields: [
      { type: "check", id: "ou_location", label: "Sited in correct, agreed location (planning & noise)" },
      { type: "check", id: "ou_solid_base", label: "Secured to a solid base" },
      { type: "check", id: "ou_clearances", label: "Installed per manufacturer clearances" },
      { type: "check", id: "ou_pipes_insulated", label: "All external pipes insulated" },
      { type: "check", id: "ou_condensate", label: "Suitable consideration made for condensate water" },
      { type: "check", id: "ou_isolators", label: "Isolators fitted by unit" },
      { type: "remarks", id: "ou_remarks", label: "Remarks" },
    ],
  },
  {
    id: "refrigeration",
    title: "Refrigeration Details",
    description: "Not required for Monobloc systems.",
    fields: [
      { type: "text", id: "ref_pipe_length", label: "Refrigeration pipe length (indoor ↔ outdoor)" },
      { type: "text", id: "ref_additional_charge", label: "Additional refrigerant charge added (if required)" },
      { type: "text", id: "ref_engineer_name", label: "Qualified refrigeration engineer name" },
      { type: "text", id: "ref_company", label: "Company name" },
      { type: "number", id: "ref_pressure_test_bar", label: "Pressure test — tested to", unit: "bar" },
      { type: "text", id: "ref_pressure_test_duration", label: "Pressure test — duration" },
      { type: "remarks", id: "ref_remarks", label: "Remarks" },
    ],
  },
  {
    id: "bivalent_connection",
    title: "Bi-Valent (Hybrid) Connection",
    description: "Type of supplementary heat source, if fitted.",
    fields: [
      {
        type: "select",
        id: "bivalent_type",
        label: "Bi-valent connection type",
        options: ["None", "Mains Gas Boiler", "LPG Boiler", "Electric Heater"],
      },
      { type: "remarks", id: "bivalent_remarks", label: "Remarks" },
    ],
  },
  {
    id: "electrical",
    title: "Electrical Connection",
    description: "Confirm power supplies are correctly sized.",
    fields: [
      { type: "check", id: "elec_ps1", label: "Power supply 1 cable & breaker sized correctly" },
      { type: "check", id: "elec_ps2", label: "Power supply 2 cable & breaker sized correctly" },
      { type: "remarks", id: "elec_remarks", label: "Remarks" },
    ],
  },
  {
    id: "controls",
    title: "Controls — System & Heat Pump",
    description: "Fitted controls, sensors and pipework.",
    fields: [
      { type: "check", id: "ctrl_auto_bypass", label: "Automatic bypass fitted" },
      { type: "text", id: "ctrl_volumiser", label: "Volumiser fitted (2-pipe) — volume (L)" },
      { type: "text", id: "ctrl_buffer_tank", label: "Buffer tank fitted (4-pipe) — volume (L)" },
      { type: "check", id: "ctrl_3rd_party", label: "3rd-party heating controls" },
      { type: "check", id: "ctrl_trv", label: "Thermostatic radiator valves" },
      { type: "check", id: "ctrl_outdoor_sensor", label: "Outdoor ambient air sensor (PAW-A2W-TSOD)" },
      { type: "check", id: "ctrl_buffer_sensor", label: "Buffer tank sensor (PAW-A2W-TSBU)" },
      { type: "check", id: "ctrl_upgrade_pcb", label: "Optional upgrade PCB (CZ-NS4P)" },
      { type: "check", id: "ctrl_room_thermistor", label: "Room thermistor (PAW-AW-TSRT)" },
      { type: "check", id: "ctrl_2port", label: "2 × 2-port valve (DHW control)" },
      { type: "check", id: "ctrl_3port", label: "3-port valve (DHW control)" },
      { type: "text", id: "ctrl_plastic_pipe", label: "Plastic pipe size (O/D) on primary circuit" },
      { type: "text", id: "ctrl_copper_pipe", label: "Copper pipe size (O/D) on primary circuit" },
      { type: "remarks", id: "ctrl_remarks", label: "Remarks" },
    ],
  },
  {
    id: "system_water",
    title: "System Check — Water Quality",
    description: "Flushing, filling and water treatment.",
    fields: [
      { type: "check", id: "sys_flushed", label: "System flushed & cleaned per BS7593:2019 & manufacturer water-quality instructions" },
      { type: "check", id: "sys_filled_pressurised", label: "System filled and pressurised" },
      { type: "text", id: "sys_inhibitor", label: "Inhibitor used" },
      { type: "check", id: "sys_glycol_used", label: "Glycol (antifreeze) used" },
      { type: "text", id: "sys_glycol_pct", label: "% of glycol in the system" },
      { type: "remarks", id: "sys_remarks", label: "Remarks" },
    ],
  },
  {
    id: "heating_zone1",
    title: "Heating Settings — Zone 1",
    description: "Outlet temperatures and compensation curve.",
    fields: [
      { type: "number", id: "z1_direct_outlet", label: "Direct outlet temperature used", unit: "°C" },
      { type: "number", id: "z1_comp_low_out", label: "Compensation: outlet temp at outdoor low", unit: "°C" },
      { type: "number", id: "z1_low_outdoor", label: "Low outdoor temperature", unit: "°C" },
      { type: "number", id: "z1_comp_high_out", label: "Compensation: outlet temp at outdoor high", unit: "°C" },
      { type: "number", id: "z1_high_outdoor", label: "High outdoor temperature", unit: "°C" },
      { type: "remarks", id: "z1_remarks", label: "Remarks" },
    ],
  },
  {
    id: "heating_zone2",
    title: "Heating Settings — Zone 2",
    description: "Zone 2 / pool settings (if applicable).",
    fields: [
      { type: "number", id: "z2_direct", label: "Direct temperature", unit: "°C" },
      { type: "number", id: "z2_comp_low_out", label: "Compensation: outlet temp at outdoor low", unit: "°C" },
      { type: "number", id: "z2_low_outdoor", label: "Low outdoor temperature", unit: "°C" },
      { type: "number", id: "z2_comp_high_out", label: "Compensation: outlet temp at outdoor high", unit: "°C" },
      { type: "number", id: "z2_high_outdoor", label: "High outdoor temperature", unit: "°C" },
      { type: "number", id: "z2_heating_off", label: "Outdoor temperature for heating OFF", unit: "°C" },
      { type: "number", id: "z2_backup_on", label: "Outdoor temp for backup heater ON", unit: "°C" },
      { type: "number", id: "z2_pool_deltat", label: "Pool Delta T" },
      { type: "remarks", id: "z2_remarks", label: "Remarks" },
    ],
  },
  {
    id: "system_setup",
    title: "System Set-Up — DHW & Timings",
    description: "Hot water targets and timings.",
    fields: [
      { type: "number", id: "dhw_target_temp", label: "Target DHW tank set temperature", unit: "°C" },
      { type: "number", id: "tank_heatup_time", label: "Tank heat-up time (max, rec. 60)", unit: "min" },
      { type: "number", id: "floor_operation_time", label: "Floor operation time (max, rec. 2)", unit: "hrs" },
      {
        type: "select",
        id: "tank_heater_type",
        label: "Tank heater type",
        options: ["Internal (Backup)", "External (Booster)"],
      },
      { type: "text", id: "tank_heater_delay", label: "Tank heater on time (delay)" },
      { type: "remarks", id: "setup_remarks", label: "Remarks" },
    ],
  },
  {
    id: "sterilization",
    title: "Sterilisation Settings",
    description: "Anti-legionella sterilisation cycle.",
    fields: [
      {
        type: "select",
        id: "steril_day",
        label: "Sterilisation day",
        options: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      },
      { type: "text", id: "steril_time", label: "Sterilisation time" },
      { type: "number", id: "steril_duration", label: "Sterilisation duration", unit: "min" },
      { type: "remarks", id: "steril_remarks", label: "Remarks" },
    ],
  },
  {
    id: "backup_heater",
    title: "Backup Heater",
    description: "Backup / immersion heater configuration.",
    fields: [
      { type: "check", id: "backup_room_heater", label: "Room heater enabled" },
      { type: "number", id: "backup_capacity", label: "Backup heater capacity (3/6/9)", unit: "kW" },
      { type: "remarks", id: "backup_remarks", label: "Remarks" },
    ],
  },
  {
    id: "bivalent_settings",
    title: "Bi-Valent (Hybrid) Settings",
    description: "Hybrid control configuration (if fitted).",
    fields: [
      {
        type: "select",
        id: "biv_control_mode",
        label: "Control mode",
        options: ["Alternative", "Parallel", "Advanced Parallel"],
      },
      { type: "number", id: "biv_temp_on", label: "Outdoor temperature for bi-valent ON", unit: "°C" },
      { type: "check", id: "biv_auto", label: "Auto bi-valent (J series only)" },
      { type: "remarks", id: "biv_remarks", label: "Remarks" },
    ],
  },
  {
    id: "perf_heating",
    title: "Performance — Heating Mode",
    description: "Recorded readings in heating mode.",
    fields: [
      { type: "number", id: "perf_h_ambient", label: "Outside ambient temperature", unit: "°C" },
      { type: "number", id: "perf_h_water_out", label: "Water outlet temperature", unit: "°C" },
      { type: "number", id: "perf_h_water_in", label: "Water inlet temperature", unit: "°C" },
      { type: "number", id: "perf_h_flow", label: "Flow rate", unit: "L/min" },
      { type: "number", id: "perf_h_comp_hz", label: "Compressor frequency", unit: "Hz" },
      { type: "remarks", id: "perf_h_remarks", label: "Remarks" },
    ],
  },
  {
    id: "perf_dhw",
    title: "Performance — DHW Mode",
    description: "Recorded readings in domestic hot water mode.",
    fields: [
      { type: "number", id: "perf_d_ambient", label: "Outside ambient temperature", unit: "°C" },
      { type: "number", id: "perf_d_tank_target", label: "Tank target temperature", unit: "°C" },
      { type: "number", id: "perf_d_water_out", label: "Water outlet temperature", unit: "°C" },
      { type: "number", id: "perf_d_water_in", label: "Water inlet temperature", unit: "°C" },
      { type: "number", id: "perf_d_flow", label: "Flow rate", unit: "L/min" },
      { type: "number", id: "perf_d_comp_hz", label: "Compressor frequency", unit: "Hz" },
      { type: "remarks", id: "perf_d_remarks", label: "Remarks" },
    ],
  },
];

export const COMMISSIONING_SECTIONS = withToggleRemarks(RAW_COMMISSIONING_SECTIONS);

export const FORM_TYPES = {
  service_report: {
    key: "service_report",
    label: "Service Report",
    shortLabel: "Service",
    pdfTitle: "Heat Pump Service Report",
    description: "Routine annual / periodic heat pump servicing and checks.",
    icon: "wrench",
    sections: SERVICE_SECTIONS,
    finalStatusOptions: FINAL_STATUS_OPTIONS,
  },
  commissioning_report: {
    key: "commissioning_report",
    label: "Commissioning Report",
    shortLabel: "Commissioning",
    pdfTitle: "Heat Pump Commissioning Report",
    description: "Full commissioning checklist for a newly installed heat pump.",
    icon: "clipboard-check",
    sections: COMMISSIONING_SECTIONS,
    finalStatusOptions: ["Commissioned", "Commissioned with advisories", "Follow-up required", "Not commissioned"],
  },
};

export const FORM_TYPE_LIST = Object.values(FORM_TYPES);

export function getForm(formType) {
  return FORM_TYPES[formType] || FORM_TYPES.service_report;
}
